import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Check, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { RoomService } from '../services/RoomService';
import type { RoomJoinRequest } from '../types';

interface RoomJoinRequestsModalProps {
    isOpen: boolean;
    onClose: () => void;
    roomId: string;
    roomTitle: string;
    /** The owner or mod acting on the queue. */
    actorId: string;
}

/**
 * The requests panel, reachable by the host AND by mods.
 *
 * A sibling of JoinRequestsModal rather than a generalisation of it: that one is
 * built around a tribe's owner-only approval and its school-email field, so a
 * shared component would have had to branch on both the permission model and the
 * payload. The subscribe/approve/deny shape is the same.
 *
 * A full room does not empty this queue. Requests sit here until someone acts on
 * them, and approving while full is refused with a message rather than the
 * request being thrown away.
 */
export function RoomJoinRequestsModal({
    isOpen, onClose, roomId, roomTitle, actorId,
}: RoomJoinRequestsModalProps) {
    const [requests, setRequests] = useState<RoomJoinRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (!isOpen || !roomId) return;
        setLoading(true);
        const unsub = RoomService.subscribeToRoomJoinRequests(roomId, (next) => {
            setRequests(next);
            setLoading(false);
        });
        return unsub;
    }, [isOpen, roomId]);

    const act = async (userId: string, approve: boolean) => {
        setBusy(prev => ({ ...prev, [userId]: true }));
        try {
            if (approve) {
                await RoomService.approveRoomJoinRequest(actorId, roomId, userId);
                toast.success('Approved');
            } else {
                await RoomService.denyRoomJoinRequest(actorId, roomId, userId);
                toast.success('Denied');
            }
        } catch (e: any) {
            toast.error(e?.message || 'That did not go through.');
        } finally {
            setBusy(prev => ({ ...prev, [userId]: false }));
        }
    };

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0"
                        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
                    />
                    <motion.div
                        initial={{ scale: 0.94, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.94, opacity: 0, y: 20 }}
                        className="relative w-full max-w-md rounded-3xl overflow-hidden flex flex-col bg-white dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 shadow-2xl"
                        style={{ maxHeight: '80vh' }}
                    >
                        <div className="p-4 flex items-center justify-between border-b border-black/10 dark:border-white/10">
                            <div className="min-w-0">
                                <h2 className="text-lg text-black dark:text-white truncate" style={{ fontWeight: 700 }}>
                                    Join Requests
                                </h2>
                                <p className="text-[#666666] dark:text-[#a0a0a0] truncate" style={{ fontSize: 11 }}>
                                    {roomTitle}
                                </p>
                            </div>
                            <Button
                                variant="ghost"
                                onClick={onClose}
                                className="w-9 h-9 p-0 rounded-full hover:bg-black/10 dark:hover:bg-white/10"
                            >
                                <X className="w-4 h-4 text-black dark:text-white" />
                            </Button>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-2">
                            {loading ? (
                                <div className="flex items-center justify-center" style={{ paddingTop: 40, paddingBottom: 40 }}>
                                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}><Loader2 className="w-6 h-6" style={{ color: '#4478ea' }} /></motion.div>
                                </div>
                            ) : requests.length === 0 ? (
                                <div className="text-center text-[#666666] dark:text-[#a0a0a0]" style={{ paddingTop: 40, paddingBottom: 40 }}>
                                    <Users className="w-10 h-10 mx-auto mb-2" style={{ opacity: 0.25 }} />
                                    <p style={{ fontSize: 13, fontWeight: 700 }}>No pending requests</p>
                                </div>
                            ) : (
                                requests.map((req) => (
                                    <div
                                        key={req.userId}
                                        className="flex items-center gap-3 p-3 rounded-2xl bg-black/5 dark:bg-white/5"
                                    >
                                        <Avatar className="w-10 h-10 shrink-0">
                                            <AvatarImage src={req.userAvatar || ''} />
                                            <AvatarFallback>{(req.userName || '?')[0]}</AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-black dark:text-white truncate" style={{ fontWeight: 700, fontSize: 14 }}>
                                                {req.userName || 'Someone'}
                                            </p>
                                            {req.userUsername && (
                                                <p className="text-[#666666] dark:text-[#a0a0a0] truncate" style={{ fontSize: 12 }}>
                                                    @{String(req.userUsername).replace(/^@/, '')}
                                                </p>
                                            )}
                                            {/* A question room's answer. Not truncated
                                                and not checked against anything — it is
                                                here to be read before deciding. */}
                                            {(req as any).answer ? (
                                                <p
                                                    className="mt-1 rounded-lg px-2 py-1 text-black dark:text-white break-words"
                                                    style={{ fontSize: 12, backgroundColor: 'rgba(127,127,127,0.12)' }}
                                                >
                                                    {(req as any).answer}
                                                </p>
                                            ) : null}
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                onClick={() => act(req.userId, false)}
                                                disabled={busy[req.userId]}
                                                className="w-9 h-9 rounded-full flex items-center justify-center bg-black/5 dark:bg-white/10 disabled:opacity-40"
                                                aria-label="Deny"
                                            >
                                                <X className="w-4 h-4 text-black dark:text-white" />
                                            </button>
                                            <button
                                                onClick={() => act(req.userId, true)}
                                                disabled={busy[req.userId]}
                                                className="w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-40"
                                                style={{ background: '#2ecc71' }}
                                                aria-label="Approve"
                                            >
                                                <Check className="w-4 h-4 text-white" />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body,
    );
}

export default RoomJoinRequestsModal;
